import { useCallback, useEffect, useRef, useState } from 'react';
import { getBackendUrl } from './backendUrl';

// ─────────────────────────────────────────────────────────────────────────────
// BACKEND HEALTH — a real measurement, because the thing it replaces was not one.
//
// The shell's `backendStatus` is set ONCE, from `authState`: if you hold a session it says
// "Backend live" and nothing ever updates it again (`routes/__root.tsx`). That is a fine answer to
// "am I signed in" and no answer at all to "is the backend healthy". A green/amber/red indicator
// driven by it would be green permanently — an instrument that cannot move, which is worse than no
// instrument, because people believe it.
//
// So this polls `/health`, an endpoint the backend already serves, and TIMES it. Latency is the
// honest signal available from the browser: it degrades gradually, which is exactly what an amber
// tier is for, and it is measured rather than asserted.
//
// WHAT THE PAYLOAD CARRIES, and why the panel shows it:
//   uptimeSec / startedAt — a backend that restarted five minutes ago explains a lot of "it was
//                           working a moment ago".
//   commit                — a clean git tree says nothing about what is DEPLOYED. Null locally.
//   DB_ENV                — WHICH DATABASE this backend is pointed at. The most valuable field
//                           here: the difference between local and a live customer database is
//                           invisible in every other part of this console.
//   CLUSTER_ID / cpus     — which instance answered, and how big it is.
// Fields absent from the response are absent from the panel; nothing is invented to fill a row.
// ─────────────────────────────────────────────────────────────────────────────

/** Coarse tiers, not a percentage. A number to one millisecond would imply a precision that a
 *  single round trip over a shared machine does not have. */
export const HEALTH_TIERS = {
  ok:       { label: 'Healthy',     color: '#2F8B4F', fill: '100%' },
  slow:     { label: 'Slow',        color: '#C48319', fill: '58%'  },
  // Still answering, but at this point the console feels broken to the person using it, so it is
  // red rather than amber. Amber means "notice this"; red means "this is why nothing works".
  degraded: { label: 'Degraded',    color: '#C53030', fill: '34%'  },
  down:     { label: 'Unreachable', color: '#C53030', fill: '14%'  },
  unknown:  { label: 'Checking',    color: '#C5C2BC', fill: '35%'  },
};

const OK_MS = 300;     // anything under this is indistinguishable from instant to an operator
const SLOW_MS = 1500;  // past this the console feels broken even though it is answering
const POLL_MS = 30_000;
const TIMEOUT_MS = 8_000;

export const tierFor = (latencyMs, failed) => {
  if (failed) return 'down';
  if (latencyMs == null) return 'unknown';
  if (latencyMs < OK_MS) return 'ok';
  if (latencyMs < SLOW_MS) return 'slow';
  // The first version of this returned 'slow' here too, which made SLOW_MS dead code and meant red
  // only ever appeared when the backend was completely unreachable — so the amber-then-red
  // progression the beacon exists to show could not actually happen.
  return 'degraded';
};

export function useBackendHealth(enabled = true) {
  const [health, setHealth] = useState({ tier: 'unknown', latencyMs: null, data: null, checkedAt: null, error: null });
  const timer = useRef(null);

  const check = useCallback(async () => {
    const started = performance.now();
    // AbortController, not a bare fetch: an unreachable backend can hang until the browser's own
    // timeout, and "we have not heard back in 8 seconds" IS the answer we want to show.
    const ctl = new AbortController();
    const kill = setTimeout(() => ctl.abort(), TIMEOUT_MS);
    try {
      const res = await fetch(`${getBackendUrl().replace(/\/$/, '')}/health`, {
        credentials: 'include',
        signal: ctl.signal,
        cache: 'no-store',
      });
      const latencyMs = Math.round(performance.now() - started);
      const data = res.ok ? await res.json().catch(() => null) : null;
      setHealth({
        tier: res.ok ? tierFor(latencyMs, false) : 'down',
        latencyMs,
        data,
        checkedAt: Date.now(),
        error: res.ok ? null : `${res.status} ${res.statusText || 'health check failed'}`,
      });
    } catch (err) {
      setHealth({
        tier: 'down', latencyMs: null, data: null, checkedAt: Date.now(),
        error: err?.name === 'AbortError' ? `No response in ${TIMEOUT_MS / 1000}s` : (err?.message || 'Unreachable'),
      });
    } finally {
      clearTimeout(kill);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    check();
    // Paused while the tab is hidden. A console left open on a second monitor overnight should not
    // spend the night polling, and the first thing it does on return is check again anyway.
    const tick = () => { if (!document.hidden) check(); };
    timer.current = setInterval(tick, POLL_MS);
    const onVisible = () => { if (!document.hidden) check(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(timer.current); document.removeEventListener('visibilitychange', onVisible); };
  }, [enabled, check]);

  return { ...health, refresh: check };
}

/** "1h 17m" / "3d 4h" / "45s" — the shape an operator reads, not 4648. */
export const formatUptime = (sec) => {
  const s = Number(sec || 0);
  if (!s) return null;
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m`;
  return `${Math.round(s)}s`;
};
